import { Box } from "@material-ui/core";
import { atom, useAtom } from "jotai";
import { TYPES } from "../../errors";
import { ifEmpty } from "../../util";
import AffirmIcons from "./Form/AffirmIcons";

const AFFIRM_MIN_AMOUNT = 200;

export function shouldShowAffirm(cart) {
  return Boolean(
    cart?.totals?.total.value >= AFFIRM_MIN_AMOUNT &&
      (cart?.address?.country === "US" ||
        cart?.address?.country === "United States"),
  );
}

export const scrollAtom = atom("");

export function useScrollAtom() {
  return useAtom(scrollAtom);
}

const vipShippingRateAdjustement = 14;

const undefinedIfEmpty = ifEmpty(undefined);

const five = /^([0-9]{5})$/;
const ten = /^([0-9]{5}-[0-9]{4})$/;

/*
 * isValidZip returns if a zipCode is a valid US zip
 *
 * @param {string} zipCode
 * @returns {boolean} result
 */
export function isValidZip(zipCode) {
  return five.test(zipCode) || ten.test(zipCode);
}

/*
 * createPurchaseInfo returns a purchaseInfo object for creating either
 * a paymentIntent or a subscription
 *
 * @param {object} initialization object { state }
 * @returns {object} purchaseInfo
 */
export function createPurchaseInfo({ state }) {
  const { contactInformation } = state;

  return {
    type: "autographPurchase",
    credentials: { email: undefinedIfEmpty(contactInformation.email) },
  };
}

const cancelInfoFor = ({ paymentIntentId, clientSecret }) =>
  paymentIntentId && clientSecret
    ? { type: "autographPurchase", paymentIntentId, clientSecret }
    : null;

export function createCancelPurchaseInfo({ state }) {
  const purchase = state.purchase ?? {};

  return purchase.purchaseToken
    ? { type: "autographPurchase", purchaseToken: purchase.purchaseToken }
    : cancelInfoFor(purchase);
}

export function confirmationFor({ useSavedCard, purchase, returnUrl }) {
  const savedCardId = purchase.defaultSource?.id;

  return useSavedCard && savedCardId
    ? {
        method: "confirmCardPayment",
        clientSecret: purchase.clientSecret,
        data: { payment_method: savedCardId },
      }
    : { method: "confirmPayment", confirmParams: { return_url: returnUrl } };
}

/*
 * labelForDefaultCard returns the label for a defaultCard
 *
 * @param {object} defaultSource { type: string, last4: string }
 * @returns {string} label
 */
export function labelForDefaultCard(defaultSource) {
  return defaultSource.card?.last4
    ? `Saved Card (ending in ${defaultSource.card?.last4})`
    : `Saved Payment Method (type ${defaultSource.type})`;
}

/*
 * labelForOtherPaymentMethod returns the label for another payment method
 *
 * @param {object} defaultSource
 * @returns {string} label
 */
export function labelForOtherPaymentMethod(cart) {
  if (!shouldShowAffirm(cart) || cart?.purchaseSubscription) {
    return "Credit Card";
  }

  return (
    <Box display="flex" alignItems="center" gridGap="16px">
      <span>Credit Card</span>
      <AffirmIcons size="small" />
    </Box>
  );
}

/*
 * shipmentOptionsFor returns whether shipment is required or should show
 * shipment choice for a cart
 *
 * @param {object} cart
 * @returns {object} shipmentOptions { shipmentRequired, showShipmentChoice }
 */
export function shipmentOptionsFor(cart) {
  if (!cart) {
    return { shipmentRequired: true, showPickupChoice: false };
  }

  const { hasNoShipping } = cart.totals;

  const showPickupChoice = cart.cartProducts.every(
    (cartProduct) => cartProduct.hasConventionTag,
  );

  return {
    shipmentRequired: !hasNoShipping,
    showPickupChoice,
  };
}

const propName = ({ propertyName, dataPath } = {}) => {
  if (propertyName) {
    return `${propertyName} `;
  }

  const last = dataPath?.split(".")?.pop();

  if (last) {
    return `${last} `;
  }

  return "";
};

export function errorMessageFrom(errors) {
  const [error] = errors;

  if (error.message.includes('should match format "password"')) {
    return `Password must be at least 6 characters long`;
  }

  if (errors.length > 1 && error.propertyName) {
    return errors
      .map(
        ({ propertyName, dataPath, message }) =>
          `${propName({ propertyName, dataPath })}invalid: ${message}`,
      )
      .join(", ");
  }

  switch (error.type) {
    case TYPES.CREATE_USER_ERROR:
      return error.message;

    case TYPES.CREATE_SIMPLE_PURCHASE_ERROR:
      return error.message;

    case TYPES.CREATE_SUBSCRIPTION_PURCHASE_ERROR:
      return error.message;

    default:
      return `Whoops! ${errors
        .map((err) => `${propName(err)}${err.message}`)
        .join(", ")}`;
  }
}

/*
 * hasValidShippingChoice returns if state has valid shipping choice
 *
 * @param {object} cartAttributes
 * @returns {boolean} result
 */
export function hasValidShippingChoice(cartAttributes) {
  return (
    cartAttributes.pickupAtConvention ||
    (!cartAttributes.pickupAtConvention &&
      (cartAttributes.purchaseSubscription === true ||
        cartAttributes.purchaseSubscription === false))
  );
}

/*
 * createPromoText returns a function that returns promo text
 *
 * @param {string} FREE Shipping text
 * @param {string} Almost free shipping text
 *
 * @returns {function} promoTextFor { cart, currentUser, state }
 */
export function createPromoText(freeText, almostFreeText) {
  return function promoTextFor({ cart }) {
    const { totals } = cart;
    const { unadjustedShippingCost } = totals;

    return unadjustedShippingCost.value === 0 ||
      unadjustedShippingCost.value <= vipShippingRateAdjustement
      ? freeText
      : `$${vipShippingRateAdjustement} ${almostFreeText}`;
  };
}

export const errorPromoTextFor = createPromoText(
  "Free Shipping?",
  "Off Shipping?",
);

/*
 * effectiveSavePreference computes the effective savePaymentInformation value,
 * accounting for checkbox visibility conditions. If the checkbox isn't visible
 * to the user, the preference is always false — unless the cart contains a
 * subscription, in which case saving is mandatory.
 *
 * @param {object} params
 * @param {object} params.cart
 * @param {boolean} params.isLoggedIn
 * @param {string|null} params.purchaseToken
 * @param {boolean} params.savePaymentInformation
 * @returns {boolean}
 */
export const effectiveSavePreference = ({
  cart,
  isLoggedIn,
  purchaseToken,
  savePaymentInformation,
}) =>
  cart?.purchaseSubscription
    ? true
    : !shouldShowAffirm(cart) && isLoggedIn && !purchaseToken
    ? Boolean(savePaymentInformation)
    : false;
